import { useAuth } from "@/context/AuthContext";
import { format } from "date-fns";
import { AlertCircleIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { Activity } from "@/lib/activity";
import { ActivityItem } from "@/components/ActivityItem";
import { Spinner } from "@/components/ui/spinner";

export interface ActivityListProps {
  category: string | null,
  date: Date | undefined,
}

export function ActivityList({ category, date }: ActivityListProps) {
  const { fetchWithAuth } = useAuth();
  const [activities, setActivities] = useState<Activity[] | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const loadActivities = () => {
    setError(undefined);
    setActivities(undefined);
    if (!category || !date) {
      return;
    }
    const params = new URLSearchParams();
    params.set("date", format(date, "yyyy-MM-dd"));
    fetchWithAuth("GET", `/categories/${category}/timetable?` + params.toString()).then(setActivities).catch(setError);
  };
  useEffect(loadActivities, [category, date]);

  if (error) {
    return (
      <Alert variant="destructive" className="w-full">
        <AlertCircleIcon />
        <AlertTitle>Failed to fetch classes</AlertTitle>
        <AlertDescription>
          {error?.message ?? "An error occurred"}
        </AlertDescription>
      </Alert>
    );
  }
  if (activities) {
    if (!activities.length) {
      return (
        <Alert variant="default" className="w-full">
          <AlertCircleIcon />
          <AlertTitle>No matching classes found</AlertTitle>
          <AlertDescription>
            🐧 Why not ask DP for a flying class instead?
          </AlertDescription>
        </Alert>
      );
    }
    return (
      <div className="flex flex-wrap w-full flex-col gap-6">
        {activities.map(activity => (
          <ActivityItem activity={activity} refreshActivities={loadActivities} />
        ))}
      </div>
    );
  }
  if (!category) {
    return (
      <Alert variant="default" className="w-full">
        <AlertCircleIcon />
        <AlertTitle>Select an activity</AlertTitle>
        <AlertDescription>
          Select an activity to view the timetable.
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <div className="flex items-center justify-center h-full">
      <Spinner className="size-8" />
    </div>
  )
}